import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { RegisterData } from '../../services/authService';

interface RegisterFormProps {
  onSubmit: (data: RegisterData) => void;
  isLoading?: boolean;
  error?: string;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSubmit, isLoading = false, error }) => {
  const navigate = useNavigate();
  const { theme, systemTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    termsAccepted: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [errors, setErrors] = useState<Partial<RegisterData> & { termsAccepted?: boolean }>({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    setMounted(true);
  }, []);

  const currentTheme = theme === 'system' ? systemTheme : theme;
  const isDarkMode = currentTheme === 'dark';

  // Mostrar modal quando houver erro do backend
  useEffect(() => {
    if (error) {
      // Limpar prefixos numéricos e bullets da mensagem
      const cleanError = error.split('\n').map(line => 
        line.replace(/^[\d•\-\s]+/, '').trim()
      ).filter(line => line).join('\n');
      setErrorMessage(cleanError || error);
      setShowErrorModal(true);
    }
  }, [error]);

  const validateForm = (): { isValid: boolean; errors: Partial<RegisterData> & { termsAccepted?: boolean } } => {
    const newErrors: Partial<RegisterData> & { termsAccepted?: boolean } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Nome é obrigatório';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Sobrenome é obrigatório';
    }

    if (!formData.email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Confirmação de senha é obrigatória';
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Senhas não coincidem';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = true;
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { isValid, errors: validationErrors } = validateForm();
    if (isValid) {
      onSubmit(formData);
    } else {
      // Mostrar modal com erros de validação
      const errorMessages = Object.values(validationErrors)
        .filter(err => err !== undefined && err !== false)
        .map(err => typeof err === 'string' ? err : 'Por favor, corrija os erros no formulário');
      
      if (errorMessages.length > 0) {
        setErrorMessage(errorMessages.join('\n'));
        setShowErrorModal(true);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    // Clear error when user starts typing
    if (errors[name as keyof RegisterData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const passwordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const getPasswordRequirements = (password: string) => {
    return {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password),
    };
  };

  const strength = passwordStrength(formData.password);
  const strengthLabels = ['Muito fraca', 'Fraca', 'Regular', 'Boa', 'Muito boa'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
  const requirements = getPasswordRequirements(formData.password);

  return (
    <>
      {/* Error Modal */}
      <Dialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 font-bold text-xl">
              Erro
            </DialogTitle>
            <DialogDescription asChild>
              <div className="mt-4">
                {errorMessage.split('\n').map((line, index) => (
                  <p key={index} className="text-red-600 font-bold text-base mb-2 last:mb-0">
                    {line}
                  </p>
                ))}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowErrorModal(false)}
              className="min-w-[100px]"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-card rounded-lg shadow-xl p-8 border border-border">
          {/* Header */}
          <div className="text-center mb-8">
            {mounted && (
              <img
                src={isDarkMode 
                  ? "/imgs/logos/EscriturIA-logo-horizontal.png"
                  : "/imgs/logos/EscriturIA-logo-horizontal-sem-slogan.png"
                }
                alt="EscriturIA Logo"
                className="h-16 w-auto object-contain mx-auto mb-4"
              />
            )}
            <h1 className="text-2xl font-bold text-secondary font-serif">
              Criar conta
            </h1>
            <p className="text-brand-text-muted mt-2">
              Junte-se à comunidade EscriturIA
            </p>
          </div>

        {/* Register Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-brand-text-primary mb-2">
                Nome
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                  errors.firstName ? 'border-destructive' : ''
                }`}
                placeholder="Nome"
                disabled={isLoading}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-brand-error-text">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-brand-text-primary mb-2">
                Sobrenome
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                  errors.lastName ? 'border-destructive' : ''
                }`}
                placeholder="Sobrenome"
                disabled={isLoading}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-brand-error-text">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-brand-text-primary mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                errors.email ? 'border-destructive' : ''
              }`}
              placeholder="seu@email.com"
              disabled={isLoading}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-brand-error-text">{errors.email}</p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-brand-text-primary mb-2">
              Senha
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                  errors.password ? 'border-destructive' : ''
                }`}
                placeholder="Sua senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary transition-colors duration-200"
                disabled={isLoading}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            
            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="mt-2">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1 flex-1 rounded ${
                        level <= strength ? strengthColors[strength - 1] : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-brand-text-muted mt-1">
                  Força da senha: {strength > 0 ? strengthLabels[strength - 1] : 'Muito fraca'}
                </p>
                
                {/* Password Requirements Checklist */}
                <div className="mt-3 space-y-1">
                  <p className="text-xs font-medium text-brand-text-primary mb-2">Requisitos da senha:</p>
                  <div className="space-y-1">
                    <div className="flex items-center text-xs">
                      <span className={`mr-2 ${requirements.minLength ? 'text-green-500' : 'text-brand-text-muted'}`}>
                        {requirements.minLength ? '✓' : '○'}
                      </span>
                      <span className={requirements.minLength ? 'text-green-500' : 'text-brand-text-muted'}>
                        Mínimo de 8 caracteres
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <span className={`mr-2 ${requirements.hasUpperCase ? 'text-green-500' : 'text-brand-text-muted'}`}>
                        {requirements.hasUpperCase ? '✓' : '○'}
                      </span>
                      <span className={requirements.hasUpperCase ? 'text-green-500' : 'text-brand-text-muted'}>
                        Pelo menos uma letra maiúscula (A-Z)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <span className={`mr-2 ${requirements.hasLowerCase ? 'text-green-500' : 'text-brand-text-muted'}`}>
                        {requirements.hasLowerCase ? '✓' : '○'}
                      </span>
                      <span className={requirements.hasLowerCase ? 'text-green-500' : 'text-brand-text-muted'}>
                        Pelo menos uma letra minúscula (a-z)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <span className={`mr-2 ${requirements.hasNumber ? 'text-green-500' : 'text-brand-text-muted'}`}>
                        {requirements.hasNumber ? '✓' : '○'}
                      </span>
                      <span className={requirements.hasNumber ? 'text-green-500' : 'text-brand-text-muted'}>
                        Pelo menos um número (0-9)
                      </span>
                    </div>
                    <div className="flex items-center text-xs">
                      <span className={`mr-2 ${requirements.hasSpecialChar ? 'text-green-500' : 'text-brand-text-muted'}`}>
                        {requirements.hasSpecialChar ? '✓' : '○'}
                      </span>
                      <span className={requirements.hasSpecialChar ? 'text-green-500' : 'text-brand-text-muted'}>
                        Pelo menos um caractere especial (@, #, $, !, etc)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {errors.password && (
              <p className="mt-1 text-sm text-brand-error-text">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="passwordConfirm" className="block text-sm font-medium text-brand-text-primary mb-2">
              Confirmar Senha
            </label>
            <div className="relative">
              <input
                type={showPasswordConfirm ? 'text' : 'password'}
                id="passwordConfirm"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                className={`w-full px-4 py-3 pr-12 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 ${
                  errors.passwordConfirm ? 'border-destructive' : ''
                }`}
                placeholder="Confirme sua senha"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-brand-text-muted hover:text-brand-text-primary transition-colors duration-200"
                disabled={isLoading}
              >
                {showPasswordConfirm ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.passwordConfirm && (
              <p className="mt-1 text-sm text-brand-error-text">{errors.passwordConfirm}</p>
            )}
          </div>

          {/* Terms and Privacy Policy */}
          <div>
            <label className="flex items-start">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleChange}
                className={`h-4 w-4 mt-1 text-secondary bg-background border-border rounded focus:ring-secondary focus:ring-2 ${
                  errors.termsAccepted ? 'border-destructive' : ''
                }`}
                disabled={isLoading}
              />
              <span className="ml-3 text-sm text-brand-text-secondary">
                Aceito os{' '}
                <a href="/termos-de-uso" className="text-brand-accent hover:text-brand-accent-hover hover:underline">
                  Termos de Uso
                </a>
                {' '}e a{' '}
                <a href="/politica-de-privacidade" className="text-brand-accent hover:text-brand-accent-hover hover:underline">
                  Política de Privacidade
                </a>
                {' '}<span className="text-red-500">*</span>
              </span>
            </label>
            {errors.termsAccepted && (
              <p className="text-sm text-brand-error-text mt-1">Você deve aceitar os termos de uso e política de privacidade</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="hero"
            size="lg"
            disabled={isLoading}
            className="w-full text-lg px-8 py-6"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Criando conta...
              </>
            ) : (
              'Criar conta'
            )}
          </Button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-brand-text-muted text-sm">
            Já tem uma conta?{' '}
            <button 
              type="button"
              onClick={() => navigate('/login')}
              className="text-brand-accent hover:text-brand-accent-hover font-semibold hover:underline transition-colors duration-200"
            >
              Fazer login
            </button>
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default RegisterForm;
